import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { CheckCircle2, CircleDashed, Clock, Globe, RefreshCw, ShieldCheck, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { CustomDomainSettingsResponse, CustomDomainStatus, DomainVerifyResult } from '@bm/contracts';
import { CUSTOM_DOMAIN_STATUS_LABELS } from '@bm/contracts';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Alert, Button, Card, Field, Input } from '../components/ui';
import { OrgTabs } from '../components/OrgTabs';

const STATUS_STYLE: Record<CustomDomainStatus, { icon: typeof CircleDashed; className: string }> = {
  none: { icon: CircleDashed, className: 'bg-slate-100 text-slate-600' },
  pending_approval: { icon: Clock, className: 'bg-amber-50 text-amber-700' },
  approved: { icon: ShieldCheck, className: 'bg-blue-50 text-blue-700' },
  active: { icon: CheckCircle2, className: 'bg-green-50 text-green-700' },
  rejected: { icon: XCircle, className: 'bg-red-50 text-red-700' },
};

function StatusBadge({ status }: { status: CustomDomainStatus }) {
  const { icon: Icon, className } = STATUS_STYLE[status];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${className}`}>
      <Icon size={13} />
      {CUSTOM_DOMAIN_STATUS_LABELS[status]}
    </span>
  );
}

/** The step-by-step "Schieberegler"-Toggle requested for step 1 — a plain
 * checkbox styled as a switch, not a new dependency. */
function Toggle({
  checked,
  onChange,
  disabled,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-3">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative h-6 w-11 shrink-0 rounded-full transition-colors disabled:opacity-50 ${
          checked ? 'bg-brand-accent' : 'bg-slate-300'
        }`}
      >
        <span
          className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
            checked ? 'translate-x-5' : 'translate-x-0.5'
          }`}
        />
      </button>
      <span className="text-sm font-medium text-slate-700">{checked ? 'Aktiviert' : 'Deaktiviert'}</span>
    </label>
  );
}

function StepCard({
  step,
  title,
  done,
  children,
}: {
  step: number;
  title: string;
  done?: boolean;
  children: ReactNode;
}) {
  return (
    <Card>
      <div className="mb-3 flex items-center gap-2.5">
        <span
          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
            done ? 'bg-green-100 text-green-700' : 'bg-brand-accent/10 text-brand-accent'
          }`}
        >
          {done ? <CheckCircle2 size={14} /> : step}
        </span>
        <h2 className="text-base font-semibold text-slate-800">{title}</h2>
      </div>
      {children}
    </Card>
  );
}

export function OrgCustomDomainPage() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const readOnly = !can('organization.edit');

  const { data } = useQuery({
    queryKey: ['org', 'custom-domain'],
    queryFn: () => api<CustomDomainSettingsResponse>('/org/custom-domain', { orgScoped: true }),
  });

  const [enabled, setEnabled] = useState(false);
  const [domain, setDomain] = useState('');
  const [saved, setSaved] = useState(false);
  const [verifyResult, setVerifyResult] = useState<DomainVerifyResult | null>(null);
  const [dnsResult, setDnsResult] = useState<DomainVerifyResult | null>(null);

  useEffect(() => {
    if (!data) return;
    setEnabled(data.enabled);
    setDomain(data.domain ?? '');
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      api<CustomDomainSettingsResponse>('/org/custom-domain', {
        method: 'PATCH',
        orgScoped: true,
        body: { enabled, domain: domain.trim() || undefined },
      }),
    onSuccess: (res) => {
      setSaved(true);
      setVerifyResult(null);
      qc.setQueryData(['org', 'custom-domain'], res);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  const checkDns = useMutation({
    mutationFn: () =>
      api<DomainVerifyResult>('/org/custom-domain/check-dns', {
        method: 'POST',
        orgScoped: true,
        body: { domain: domain.trim() },
      }),
    onSuccess: (res) => setDnsResult(res),
    onError: (e) =>
      setDnsResult({ success: false, detail: e instanceof ApiError ? e.message : 'DNS-Prüfung fehlgeschlagen.' }),
  });

  const requestApproval = useMutation({
    mutationFn: () => api<CustomDomainSettingsResponse>('/org/custom-domain/request-approval', { method: 'POST', orgScoped: true }),
    onSuccess: (res) => qc.setQueryData(['org', 'custom-domain'], res),
  });

  const verify = useMutation({
    mutationFn: () => api<DomainVerifyResult>('/org/custom-domain/verify', { method: 'POST', orgScoped: true }),
    onSuccess: (res) => {
      setVerifyResult(res);
      if (res.success) void qc.invalidateQueries({ queryKey: ['org', 'custom-domain'] });
    },
  });

  const status = data?.status ?? 'none';
  const domainUnchangedFromSaved = domain.trim() === (data?.domain ?? '') && enabled === (data?.enabled ?? false);
  const canRequestApproval = !!data?.domain && data.enabled && (status === 'none' || status === 'rejected') && domainUnchangedFromSaved;

  return (
    <div className="space-y-4">
      <h1 className="hidden text-xl font-semibold md:block">Verein</h1>
      <OrgTabs />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-800">
            <Globe size={20} className="text-brand-accent" />
            Eigene Domain
          </h2>
          <p className="text-sm text-slate-500">
            Ruft jemand eure eigene Domain auf, sieht er direkt eure personalisierte Login-Seite —
            und die Domain steht in der Browserzeile statt der allgemeinen Plattform-Adresse.
          </p>
        </div>
        {data && <StatusBadge status={status} />}
      </div>

      <StepCard step={1} title="Eigene Domain eintragen" done={!!data?.domain}>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <Toggle checked={enabled} onChange={setEnabled} disabled={readOnly} label="Eigene Domain aktivieren" />
          <Field label="Domain">
            <Input
              value={domain}
              disabled={readOnly}
              onChange={(e) => {
                setDomain(e.target.value);
                setDnsResult(null);
              }}
              placeholder="verein.beispiel.de"
            />
          </Field>
          {!readOnly && (
            <Button type="submit" disabled={save.isPending}>
              Speichern
            </Button>
          )}
          {saved && <Alert kind="success">Gespeichert.</Alert>}
          {save.isError && (
            <Alert>{save.error instanceof ApiError ? save.error.message : 'Speichern fehlgeschlagen.'}</Alert>
          )}

          {data && (
            <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
              <p className="mb-1.5 font-semibold text-slate-700">So richtest du die Domain bei deinem Anbieter ein:</p>
              <p>
                Lege bei deinem Domain-Anbieter (z. B. denic, IONOS, Cloudflare, …) einen{' '}
                <strong>CNAME-Eintrag</strong> für {domain.trim() ? <code>{domain.trim()}</code> : 'deine Domain'} an,
                der auf <code className="rounded bg-white px-1 py-0.5">{data.appHost}</code> zeigt. Geht das bei einer
                Domain ohne Subdomain (z. B. <code>verein.de</code> statt <code>www.verein.de</code>) bei deinem
                Anbieter nicht, nutze stattdessen einen A-Eintrag mit der IP-Adresse — dein Anbieter oder unser
                Plattformadministrator kann dir die passende Adresse nennen. DNS-Änderungen können bis zu ein paar
                Stunden brauchen, bis sie überall wirken.
              </p>
            </div>
          )}

          {!readOnly && (
            <div className="space-y-2">
              <Button
                type="button"
                variant="ghost"
                className="gap-1.5"
                disabled={!domain.trim() || checkDns.isPending}
                onClick={() => checkDns.mutate()}
              >
                <RefreshCw size={14} className={checkDns.isPending ? 'animate-spin' : ''} />
                DNS prüfen
              </Button>
              {dnsResult && <Alert kind={dnsResult.success ? 'success' : 'error'}>{dnsResult.detail}</Alert>}
            </div>
          )}
        </form>
      </StepCard>

      <StepCard step={2} title="Domain von Plattformadministrator freigeben lassen" done={status === 'active' || status === 'approved'}>
        {status === 'none' && (
          <p className="text-sm text-slate-500">
            Trage zuerst eine Domain ein und speichere (Schritt 1), dann kannst du hier die Freigabe anfragen.
          </p>
        )}
        {(status === 'none' || status === 'rejected') && data?.domain && (
          <div className="space-y-2">
            {status === 'rejected' && (
              <Alert>
                Abgelehnt{data.rejectionReason ? `: ${data.rejectionReason}` : '.'} Nach einer Anpassung kannst du
                erneut anfragen.
              </Alert>
            )}
            {!readOnly && (
              <Button disabled={!canRequestApproval || requestApproval.isPending} onClick={() => requestApproval.mutate()}>
                {requestApproval.isPending ? 'Wird angefragt …' : 'Freigabe anfragen'}
              </Button>
            )}
            {!domainUnchangedFromSaved && (
              <p className="text-xs text-slate-400">Bitte zuerst Schritt 1 speichern.</p>
            )}
          </div>
        )}
        {status === 'pending_approval' && (
          <p className="text-sm text-slate-600">
            Warte auf Freigabe durch einen Plattformadministrator
            {data?.requestedAt && ` (angefragt am ${new Date(data.requestedAt).toLocaleString('de-DE')})`}.
          </p>
        )}
        {(status === 'approved' || status === 'active') && (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Freigegeben. Ein Plattformadministrator trägt die Domain jetzt (falls noch nicht geschehen) manuell bei
              der App in Coolify ein — das ist ein einmaliger, manueller Schritt außerhalb dieser App.
            </p>
            {!readOnly && (
              <Button variant="ghost" className="gap-1.5" disabled={verify.isPending} onClick={() => verify.mutate()}>
                <RefreshCw size={14} className={verify.isPending ? 'animate-spin' : ''} />
                Jetzt prüfen
              </Button>
            )}
            {verifyResult && (
              <Alert kind={verifyResult.success ? 'success' : 'error'}>{verifyResult.detail}</Alert>
            )}
          </div>
        )}
      </StepCard>

      <StepCard step={3} title="Loginseite einrichten" done={!!data?.domain}>
        <p className="text-sm text-slate-600">
          Unter der eigenen Domain zeigt die Login-Seite automatisch das gleiche Design wie eure Registerseite —
          Bild, Überschrift, Begrüßungstext und Farben. Das ist dieselbe Einstellung, keine zweite Konfiguration
          nötig.
        </p>
        <Link
          to="/organization/auth-design"
          className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-brand-accent hover:underline"
        >
          Design Anmelde/Registerseite bearbeiten →
        </Link>
      </StepCard>
    </div>
  );
}
